import * as React from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import LineGuidedtoursIcon from 'LuxKit/Icons/line/LineGuidedtoursIcon'
import LineTheatreIcon from 'LuxKit/Icons/line/LineTheatreIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineWellnessIcon from 'LuxKit/Icons/line/LineWellnessIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import { EXP_CATEGORY_LUX_PLUS_ID } from 'constants/experience'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'

interface Props {
  categories: Array<App.ExperienceItemCategory>
}

/**
 * Map of parent category ids to the icon for them
 */
const parentCategoryToIconMap: Record<number, React.ReactElement> = {
  // Wine & dining
  1: <LineRestaurantIcon />,
  // Top activities
  9: <LineHotAirBalloonIcon />,
  // Indulge yourself
  31: <LineWellnessIcon />,
  // Attractions & tickets
  18: <LineTicketIcon />,
  // Live events
  27: <LineTheatreIcon />,
  // Airport lounges
  35: <LinePlaneDepartureIcon />,
  // Lux exclusive
  40: <LineStarIcon />,
  // Day tours
  48: <LineGuidedtoursIcon />,
  // Gift Inspiration
  56: <LineGiftIcon />,
  // LuxPlus
  [EXP_CATEGORY_LUX_PLUS_ID]: <LineAwardIcon />,
}

export default function ExperienceCategoryLabel(props: Props) {
  const { categories } = props
  const intl = useIntl()

  const translatedCategoryNames = useMemo<Record<number, string>>(() => ({
    1: intl.formatMessage({
      id: 'TniTSblvF4L5k_att_2i',
      defaultMessage: 'Wine & dining',
    }),
    9: intl.formatMessage({
      id: 'DQ2p5aptC-pgnZdv9N4-',
      defaultMessage: 'Top activities',
    }),
    31: intl.formatMessage({
      id: 'xpxrcGCfG5Xlu0ERchaF',
      defaultMessage: 'Indulge yourself',
    }),
    18: intl.formatMessage({
      id: 'C_yW__5xSYtAJiJ6-cdp',
      defaultMessage: 'Attractions & tickets',
    }),
    27: intl.formatMessage({
      id: '8n_GO_Ijn5qVY2MzOmQv',
      defaultMessage: 'Live events',
    }),
    35: intl.formatMessage({
      id: 'e7Lmo44oa9OJcTC_xtg3',
      defaultMessage: 'Airport lounges',
    }),
    40: intl.formatMessage({
      id: '0qWwRASQulyW3VviXgHI',
      defaultMessage: 'Lux exclusive',
    }),
    48: intl.formatMessage({
      id: '8r1WRDg05R-GKkGjHcLg',
      defaultMessage: 'Day tours',
    }),
    56: intl.formatMessage({
      id: 'CT2dTWpfzH6JBeDY9362',
      defaultMessage: 'Gift Inspiration',
    }),
    [EXP_CATEGORY_LUX_PLUS_ID]: intl.formatMessage({
      id: 'KQ8_aNSPy-5YZdsWhRtO',
      defaultMessage: 'LuxPlus',
    }),
  }), [intl])

  if (!categories?.length) {
    return null
  }

  const category = categories.find(category => category.level === 'parent') ?? categories[0]
  const categoryId = category?.parentId ?? category.id
  const icon = parentCategoryToIconMap[categoryId] ?? <LineStarIcon />
  const label = translatedCategoryNames[categoryId] ?? category.name

  return <StructureLabel variant="standard" startIcon={icon}>{label}</StructureLabel>
}
